/**
 * Shared chrome for City Planning Daily / Hotels / Tours — mirrors iOS footer.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { cityPlanningEn } from '../../content/cityPlanning.en';
import type { Trip } from '../../types/client';
import { tripWebHref } from '../../services/tripsService';
import styles from './CityPlanning.module.css';

export type CityPlanningSection = 'daily' | 'hotels' | 'tours';

export function planHref(
  userId: string,
  trip: Trip,
  cityVisitId: string,
  section: CityPlanningSection = 'daily'
): string {
  const base = tripWebHref(userId, trip);
  const q = `cityVisitId=${encodeURIComponent(cityVisitId)}`;
  if (section === 'hotels') return `${base}/plan/hotels?${q}`;
  if (section === 'tours') return `${base}/plan/tours?${q}`;
  return `${base}/plan?${q}`;
}

interface CityPlanningChromeProps {
  trip: Trip | null;
  userId: string | null | undefined;
  cityName: string;
  cityVisitId: string;
  section: CityPlanningSection;
  canEdit?: boolean;
  onSearchPlaces?: () => void;
  children: React.ReactNode;
}

export const CityPlanningChrome: React.FC<CityPlanningChromeProps> = ({
  trip,
  userId,
  cityName,
  cityVisitId,
  section,
  canEdit,
  onSearchPlaces,
  children,
}) => {
  const navigate = useNavigate();

  const handleBack = (): void => {
    navigate(trip && userId ? tripWebHref(userId, trip) : '/trips', {
      replace: true,
    });
  };

  const go = (next: CityPlanningSection): void => {
    if (!trip || !userId || !cityVisitId) return;
    navigate(planHref(userId, trip, cityVisitId, next));
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <Button
            type="link"
            className={styles.backLink}
            onClick={handleBack}
          >
            <ArrowLeftOutlined aria-hidden />
            {cityPlanningEn.back}
          </Button>
          <h1 className={styles.navTitle}>
            {cityPlanningEn.pageTitle} — {cityName}
          </h1>
          {canEdit && onSearchPlaces ? (
            <Button
              type="text"
              className={styles.searchBtn}
              icon={<SearchOutlined />}
              aria-label={cityPlanningEn.searchPlaces}
              onClick={onSearchPlaces}
            />
          ) : null}
        </div>
        {children}
      </div>
      <nav className={styles.footerNav} aria-label="City planning">
        <button
          type="button"
          className={`${styles.footerTab} ${section === 'daily' ? styles.footerTabActive : ''}`}
          disabled={section === 'daily'}
          onClick={() => go('daily')}
        >
          {cityPlanningEn.footerDaily}
        </button>
        <button
          type="button"
          className={`${styles.footerTab} ${section === 'hotels' ? styles.footerTabActive : ''}`}
          onClick={() => go('hotels')}
        >
          {cityPlanningEn.footerHotels}
        </button>
        <button
          type="button"
          className={`${styles.footerTab} ${section === 'tours' ? styles.footerTabActive : ''}`}
          onClick={() => go('tours')}
        >
          {cityPlanningEn.footerTours}
        </button>
      </nav>
    </div>
  );
};
